import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useIsHiddenForPreview } from "../contexts/PreviewHiddenContext";

// Several places show/hide this in immediate back-to-back sequence rather
// than one continuous loading state - e.g. TournamentDrawStages.js shows
// its own PageLoader while fetching the stage list, then swaps in
// TournamentGroups.js/TournamentGrids.js, which shows *its own* separate
// PageLoader while fetching that stage's actual rows. Each is a genuine,
// independent mount/unmount (different components, no shared state), so
// without this the fill animation restarted from 0% every time - reading
// as "it loads twice" once the bar became an actual progress fill instead
// of a spinner (a spinner replaying from its own start is far less
// noticeable than a progress bar visibly snapping back to empty).
// Module-level, not component state - the whole point is surviving a real
// unmount/remount of this component.
//
// A reference count, not a "last unmounted at" timestamp (tried first,
// see git history): the outgoing PageLoader's cleanup is a *passive*
// effect, which for a same-commit swap (TournamentDrawStages unmounting
// its own PageLoader in the very same render that mounts
// TournamentGroups' replacement) only runs *after* the whole new tree has
// already rendered - including the new instance's own useState
// initializer below. A timestamp written in that cleanup was therefore
// still stale (0) at the exact moment the new instance needed to read it,
// so that specific transition always measured as a real gap and reset to
// 0%. activeCount has no such ordering problem: it's incremented
// synchronously during render (see the initializer below), so a new
// mount's very first read of it already reflects any sibling that is
// mounted-but-not-yet-cleaned-up, regardless of which commit each side
// lands in.
let activeCount = 0;
let lastStartedAt = 0;
let resetTimer = null;
// Any gap shorter than this reads as "still the same loading sequence,
// just handed off between components" - long enough to cover a quick
// hand-off between sequential fetches, short enough that a genuinely new,
// later loading state (the user did something else in between) still
// starts its own fresh fill from 0%. Only matters once activeCount has
// actually dropped to 0 - see the cleanup effect below.
const CONTINUITY_WINDOW_MS = 500;

// A thin indeterminate bar pinned to the very top of the viewport, not a
// centered spinner (what this rendered before) - top:0 is unambiguous
// regardless of the page's real height, whereas centering needs to know
// that height to place itself, which inside the "Screen size" preview
// iframe depends on TournamentPreviewFrame.js's own async content-height
// measurement (see its own comments) - while that hasn't settled yet
// (exactly the moment a loader is shown), a centered spinner had nothing
// reliable to center against and could render well off from the real
// middle of the visible content. Portaled straight into <body> for the
// same reason as before: a CSS `transform`/`translate`/`filter` on any
// ancestor (even a mid-animation one, e.g. TournamentSwipeTabs' tab-enter
// animation) makes that ancestor the containing block for a fixed-position
// descendant instead of the viewport - rendered outside the whole
// component tree, this sidesteps that regardless of what animates
// anywhere above it.
const PageLoader = () => {
    // Same portal-escapes-display:none problem as BottomSheetPanel.jsx/
    // CustomModal.jsx - App.js hides the background Tournament copy behind
    // the "Screen size" preview with display:none, which has no effect on
    // this portal. Without this check, that hidden instance's own loading
    // bar stayed visibly floating over the preview whenever it was
    // fetching too, on top of the one already showing correctly inside the
    // preview iframe - two loaders on screen at once. See
    // PreviewHiddenContext.js.
    const isHiddenForPreview = useIsHiddenForPreview();

    // Lazy initializer - runs exactly once for this mount instance, not on
    // every re-render while it stays mounted. If another instance is (or,
    // within the grace window, very recently was) active, treat this as a
    // continuation of the same animation cycle (negative animation-delay
    // jumps the fill straight to wherever it would already be) instead of
    // restarting at 0%.
    // The hidden background Tournament copy (see the check below) mounts
    // and unmounts this on its own schedule, unrelated to whatever the
    // visible copy is doing - letting it touch the same module-level
    // continuity state could make the visible bar's fill jump for a
    // reason the user can't see on screen at all. It never renders
    // anything regardless, so its own timing doesn't need tracking.
    const [delayMs] = useState(() => {
        if (isHiddenForPreview) return 0;
        // A pending reset means the previous instance already dropped
        // activeCount to 0 and we're still inside its grace window - this
        // new mount arrived in time, so cancel that reset and continue
        // rather than starting fresh.
        if (resetTimer) {
            clearTimeout(resetTimer);
            resetTimer = null;
        }
        const now = Date.now();
        const isContinuation = activeCount > 0 && lastStartedAt;
        activeCount += 1;
        if (!isContinuation) lastStartedAt = now;
        return now - lastStartedAt;
    });

    useEffect(() => {
        if (isHiddenForPreview) return;
        return () => {
            activeCount = Math.max(0, activeCount - 1);
            if (activeCount === 0) {
                resetTimer = setTimeout(() => {
                    lastStartedAt = 0;
                    resetTimer = null;
                }, CONTINUITY_WINDOW_MS);
            }
        };
    }, [isHiddenForPreview]);

    if (isHiddenForPreview) return null;

    // Inside the "Screen size" preview iframe (App.js's own preview_frame=1
    // check - same query param, read the same way, since that flag isn't
    // threaded down through context/props this deep), this component's own
    // document is the FRAMED one - portaling to *its* document.body still
    // pins the bar to the top of the iframe's own box, which visually sits
    // below the real outer header/nav, not the true top of the browser
    // viewport. Every other preview-aware piece (BottomSheetPanel, modals)
    // solves the equivalent problem by rendering once, outside the preview
    // entirely; this can't do that the same way since the loading state it
    // reflects only exists inside the framed copy (the outer copy is
    // hidden/inert - see isHiddenForPreview above). Portaling straight to
    // window.top's document instead reaches the *real* page (same origin,
    // so this is plain synchronous DOM access, no postMessage needed) and
    // keeps the bar pinned to the actual viewport top regardless of preview
    // width - same "doesn't move" behavior the header/bottom nav already
    // have. A no-op when not framed (window.top is just window itself).
    const isPreviewFrame = new URLSearchParams(window.location.search).get("preview_frame") === "1";
    const portalTarget = isPreviewFrame ? window.top.document.body : document.body;

    return ReactDOM.createPortal(
        <div className="top-loading-bar" style={{ animationDelay: `-${delayMs}ms` }} />,
        portalTarget
    );
}

export default PageLoader